import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ArrowRight, Home, Users, BookOpen, Layers, MessageCircle } from 'lucide-react';
import { trackEvent } from '@/utils/analytics';

export function NotFoundPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      trackEvent('404_search', { query: searchQuery });
      navigate('/');
    }
  };

  const destinations = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'Practitioner Certification', path: '/practitioner-certification', icon: BookOpen },
    { name: 'Team Effectiveness', path: '/team-effectiveness', icon: Users },
    { name: 'The FIVE LENS Framework', path: '/framework', icon: Layers },
    { name: 'Contact Us', path: '/contact', icon: MessageCircle },
  ];

  return (
    <main className="pt-32 pb-24 min-h-screen bg-ground-alt flex flex-col items-center">
      <Helmet>
        <title>Page Not Found | FIVE LENS Enneagram</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="max-w-2xl mx-auto px-6 w-full text-center mb-12">
        <h1 className="text-8xl font-serif text-ink/10 mb-4 tracking-tighter">404</h1>
        <h2 className="text-3xl font-serif text-ink mb-4">Page not found</h2>
        <p className="text-ink/70 mb-8">
          The page you are looking for might have been removed, had its name changed, or is temporarily unavailable.
        </p>

        <form onSubmit={handleSearch} className="relative max-w-md mx-auto mb-16">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search our site..."
            className="w-full pl-12 pr-4 py-4 bg-white border border-ground-deep focus:border-rule-3 focus:ring-1 focus:ring-rule-3 outline-none text-ink"
          />
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-ink/40" />
          <button type="submit" className="absolute right-4 top-1/2 -translate-y-1/2 text-rule-3 font-bold uppercase text-xs tracking-wider">
            Search
          </button>
        </form>
      </div>

      <div className="max-w-4xl mx-auto px-6 w-full">
        <h3 className="text-sm font-bold uppercase tracking-widest text-ink/50 text-center mb-8">Popular Destinations</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {destinations.map((dest) => {
            const Icon = dest.icon;
            return (
              <Link
                key={dest.path}
                to={dest.path}
                onClick={() => trackEvent('404_popular_link_click', { destination: dest.name })}
                className="flex items-center gap-4 p-6 bg-white border border-ground-deep hover:border-rule-3 transition-colors group"
              >
                <div className="w-10 h-10 bg-ground-alt flex items-center justify-center group-hover:bg-rule-3/20 transition-colors">
                  <Icon className="w-5 h-5 text-ink group-hover:text-rule-3 transition-colors" />
                </div>
                <span className="font-bold text-sm text-ink">{dest.name}</span>
                <ArrowRight className="w-4 h-4 ml-auto text-rule-3 opacity-0 group-hover:opacity-100 transition-opacity" />
              </Link>
            );
          })}
        </div>
      </div>
    </main>
  );
}
